import React, { useState, useEffect, useRef } from 'react';
import { getAuth } from 'firebase/auth';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { 
  collection, 
  query, 
  orderBy, 
  onSnapshot, 
  doc, 
  updateDoc,
  getDoc,
  serverTimestamp,
  addDoc,
  increment
} from 'firebase/firestore';
import { IoIosArrowBack, IoIosSend } from 'react-icons/io';
import { BsThreeDotsVertical } from 'react-icons/bs';
import defaultAvatar from '../assets/svg/images/default-avatar.jpg';
import MessageItem from '../components/MessageItem';
import TypingIndicator from '../components/TypingIndicator';

export default function Chat() {
  const { chatId } = useParams();
  const auth = getAuth();
  const navigate = useNavigate();
  
  // State
  const [currentUserId, setCurrentUserId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [recipient, setRecipient] = useState(null);
  const [messageText, setMessageText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [onlineStatus, setOnlineStatus] = useState(false);
  const [loading, setLoading] = useState(true);
  
  const messagesEndRef = useRef(null);

  // Set current user
  useEffect(() => {
    if (!auth.currentUser?.uid) {
      navigate('/sign-in');
      return;
    }
    setCurrentUserId(auth.currentUser.uid);
  }, [auth.currentUser, navigate]);

  // Load chat data
  useEffect(() => {
    if (!chatId || !currentUserId) return;

    setLoading(true);
    
    const chatRef = doc(db, 'chats', chatId);
    const unsubscribeChat = onSnapshot(chatRef, async (docSnap) => {
      try {
        if (!docSnap.exists()) {
          throw new Error('Chat not found');
        }

        const chatData = docSnap.data();
        
        // Get recipient ID
        const recipientId = chatData.participants.find(id => id !== currentUserId);
        if (!recipientId) {
          throw new Error('Invalid chat participants');
        }

        // Load recipient data
        const recipientDoc = await getDoc(doc(db, 'users', recipientId));
        if (!recipientDoc.exists()) {
          throw new Error('Recipient not found');
        }

        const recipientData = recipientDoc.data();
        setRecipient({
          id: recipientId,
          name: recipientData.name,
          photoURL: recipientData.photoURL || defaultAvatar,
          online: recipientData.online || false
        });

        // Mark messages as read
        if (chatData.unreadCount?.[currentUserId] > 0) {
          await updateDoc(chatRef, {
            [`unreadCount.${currentUserId}`]: 0
          });
        }

        setLoading(false);
      } catch (error) {
        console.error('Chat error:', error);
        setLoading(false);
        navigate('/messages');
      }
    });

    return () => unsubscribeChat();
  }, [chatId, currentUserId, navigate]);

  // Load messages
  useEffect(() => {
    if (!chatId) return;

    const q = query(
      collection(db, 'chats', chatId, 'messages'),
      orderBy('timestamp', 'asc')
    );

    const unsubscribeMessages = onSnapshot(q, (snapshot) => {
      const messagesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setMessages(messagesData);
    });

    return () => unsubscribeMessages();
  }, [chatId]);

  // Listen for typing status
  useEffect(() => {
    if (!chatId || !recipient?.id) return;

    const typingRef = doc(db, 'chats', chatId, 'typing', recipient.id);
    const unsubscribeTyping = onSnapshot(typingRef, (docSnap) => {
      setIsTyping(docSnap.exists() ? docSnap.data().isTyping : false);
    });

    return () => unsubscribeTyping();
  }, [chatId, recipient]);

  // Listen for online status
  useEffect(() => {
    if (!recipient?.id) return;

    const statusRef = doc(db, 'status', recipient.id);
    const unsubscribeStatus = onSnapshot(statusRef, (docSnap) => {
      setOnlineStatus(docSnap.exists() ? docSnap.data().state === 'online' : false);
    });

    return () => unsubscribeStatus();
  }, [recipient]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Send message
  const sendMessage = async (e) => {
    e.preventDefault();
    if (!messageText.trim() || !chatId || !currentUserId || !recipient) return;

    try {
      // Add message to Firestore
      const messagesRef = collection(db, 'chats', chatId, 'messages');
      await addDoc(messagesRef, {
        senderId: currentUserId,
        text: messageText,
        timestamp: serverTimestamp(),
        status: 'sent'
      });

      // Update chat metadata
      const chatRef = doc(db, 'chats', chatId);
      await updateDoc(chatRef, {
        lastMessage: messageText,
        lastMessageTimestamp: serverTimestamp(),
        lastMessageSenderId: currentUserId,
        [`unreadCount.${recipient.id}`]: increment(1)
      });

      setMessageText('');
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  if (loading) {
    return (
      <div className="bg-black text-white h-screen flex items-center justify-center">
        <div className="animate-pulse">Loading chat...</div>
      </div>
    );
  }

  return (
    <div className="bg-black text-white h-screen flex flex-col">
      {/* Header */}
      <div className="bg-gray-900 p-3 flex items-center border-b border-gray-800">
        <button 
          onClick={() => navigate('/messages')}
          className="mr-2 text-white"
        >
          <IoIosArrowBack size={24} />
        </button>
        
        <img
          src={recipient?.photoURL || defaultAvatar}
          alt={recipient?.name}
          className="w-10 h-10 rounded-full mr-3 object-cover"
        />
        
        <div className="flex-1">
          <h2 className="font-semibold">{recipient?.name || 'Unknown'}</h2>
          <p className="text-xs text-gray-400">
            {isTyping ? 'typing...' : (onlineStatus ? 'online' : 'offline')}
          </p>
        </div>
        
        <button className="text-gray-400 hover:text-white">
          <BsThreeDotsVertical size={20} />
        </button>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-4 bg-gray-800">
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full">
            <p className="text-gray-400">No messages yet. Say hello!</p>
          </div>
        ) : (
          messages.map((message) => (
            <MessageItem
              key={message.id}
              message={message}
              isOwnMessage={message.senderId === currentUserId}
              recipientAvatar={recipient?.photoURL || defaultAvatar}
            />
          ))
        )}
        {isTyping && <TypingIndicator />}
        <div ref={messagesEndRef} />
      </div>

      {/* Message Input */}
      <form onSubmit={sendMessage} className="bg-gray-900 p-3 flex items-center">
        <input
          type="text"
          value={messageText}
          onChange={(e) => setMessageText(e.target.value)}
          placeholder="Type a message..."
          className="flex-1 bg-gray-800 text-white rounded-full px-4 py-2 focus:outline-none focus:ring-1 focus:ring-green-500"
        />
        <button
          type="submit"
          disabled={!messageText.trim()}
          className="ml-2 bg-green-600 text-white p-2 rounded-full disabled:opacity-50"
        >
          <IoIosSend size={20} />
        </button>
      </form>
    </div>
  );
}